/**
 * Main-thread client for the ribbon worker ('@nanome-lite/ribbon/client').
 *
 * `build` has the engine's builder shape (model, residueIndices, colorOf, options) and resolves with the meshes, so
 * it can be passed straight to engine.setRibbonBuilder (packages/render accepts builders that return a Promise).
 * Each NanomeModel's topology is posted once (WeakMap model -> id; released when the model is garbage-collected);
 * every build sends a copy of the current coordinates, the residue list and one colour per residue, all
 * transferred. Colours are read synchronously inside `build`, so the caller's colour callback never outlives the
 * call. Without Worker (Node, tests, a CSP that blocks workers) or after a worker failure the same handler runs
 * in-thread.
 */
import type { RibbonBuildOptions, RibbonMeshBuffers, RibbonSourceModel } from './types';
import { createRibbonWorkerHandler, ribbonTopology, type FromRibbonWorker, type ToRibbonWorker } from './worker';

export interface RibbonClientOptions {
  /** a Worker, a factory for one, or false to build in-thread; default: new Worker(./worker.ts, module) */
  worker?: Worker | (() => Worker) | false;
}

export interface RibbonClient {
  readonly mode: 'worker' | 'inline';
  build(model: RibbonSourceModel, residueIndices: Uint32Array, colorOf: (residueIndex: number) => [number, number, number], options: RibbonBuildOptions): Promise<RibbonMeshBuffers[]>;
  /** rejects pending builds and terminates the worker */
  dispose(): void;
}

interface Pending { resolve: (m: RibbonMeshBuffers[]) => void; reject: (e: Error) => void }

/** The request for one build (exported for tests): colours resolved now, coordinates and residues copied. */
export function packBuild(id: number, modelId: number, model: RibbonSourceModel, residueIndices: Uint32Array, colorOf: (residueIndex: number) => [number, number, number], options: RibbonBuildOptions): { msg: ToRibbonWorker; transfer: ArrayBuffer[] } {
  const colors = new Float32Array(residueIndices.length * 3);
  for (let k = 0; k < residueIndices.length; k++) {
    const c = colorOf(residueIndices[k]!);
    colors[k * 3] = c[0]; colors[k * 3 + 1] = c[1]; colors[k * 3 + 2] = c[2];
  }
  const xyz = model.xyz.slice();
  const residues = residueIndices.slice();
  const opts: RibbonBuildOptions = { stepsPerResidue: options.stepsPerResidue };
  if (options.reproduceSheetSmoothingQuirk !== undefined) opts.reproduceSheetSmoothingQuirk = options.reproduceSheetSmoothingQuirk;
  return {
    msg: { type: 'build', id, modelId, xyz, residueIndices: residues, colors, options: opts },
    transfer: [xyz.buffer, residues.buffer, colors.buffer] as ArrayBuffer[],
  };
}

export function createRibbonClient(options: RibbonClientOptions = {}): RibbonClient {
  const pending = new Map<number, Pending>();
  const modelIds = new WeakMap<object, number>();
  /** model ids the current transport knows */
  const sent = new Set<number>();
  let nextId = 1;
  let nextModel = 1;
  let disposed = false;

  const onMessage = (msg: FromRibbonWorker) => {
    const p = pending.get(msg.id);
    if (!p) return;
    pending.delete(msg.id);
    if (msg.type === 'result') p.resolve(msg.meshes);
    else p.reject(new Error(msg.message));
  };

  let post: (msg: ToRibbonWorker, transfer?: ArrayBuffer[]) => void;
  let worker: Worker | undefined;
  let mode: 'worker' | 'inline' = 'inline';

  const goInline = () => {
    const handle = createRibbonWorkerHandler({ postMessage: (m) => onMessage(m) }, { transfer: false });
    post = (msg) => handle(msg);
    mode = 'inline';
    sent.clear();
  };

  if (options.worker !== false) {
    try {
      if (options.worker instanceof Object && 'postMessage' in options.worker) worker = options.worker as Worker;
      else if (typeof options.worker === 'function') worker = options.worker();
      else if (typeof Worker !== 'undefined') worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    } catch {
      worker = undefined;
    }
  }
  if (worker) {
    const w = worker;
    mode = 'worker';
    w.onmessage = (e: MessageEvent<FromRibbonWorker>) => onMessage(e.data);
    w.onerror = (e: ErrorEvent) => {
      // A failing worker (blocked script, crash): reject what it held and build in-thread from now on.
      const err = new Error(e.message || 'ribbon worker failed');
      for (const p of pending.values()) p.reject(err);
      pending.clear();
      w.terminate();
      worker = undefined;
      goInline();
    };
    post = (msg, transfer) => w.postMessage(msg, transfer ?? []);
  } else {
    goInline();
  }

  // Forget a model in the worker once the NanomeModel itself is gone.
  const registry = typeof FinalizationRegistry !== 'undefined'
    ? new FinalizationRegistry<number>((id) => { if (!disposed && sent.delete(id)) post({ type: 'release', modelId: id }); })
    : null;

  const modelIdOf = (model: RibbonSourceModel): number => {
    let id = modelIds.get(model);
    if (id === undefined) {
      id = nextModel++;
      modelIds.set(model, id);
      registry?.register(model, id);
    }
    if (!sent.has(id)) {
      post({ type: 'model', modelId: id, topology: ribbonTopology(model) });
      sent.add(id);
    }
    return id;
  };

  return {
    get mode() { return mode; },
    build(model, residueIndices, colorOf, buildOptions) {
      if (disposed) return Promise.reject(new Error('ribbon client disposed'));
      if (residueIndices.length === 0) return Promise.resolve([]);
      const id = nextId++;
      const { msg, transfer } = packBuild(id, modelIdOf(model), model, residueIndices, colorOf, buildOptions);
      return new Promise<RibbonMeshBuffers[]>((resolve, reject) => {
        pending.set(id, { resolve, reject });
        post(msg, transfer);
      });
    },
    dispose() {
      disposed = true;
      const err = new Error('ribbon client disposed');
      for (const p of pending.values()) p.reject(err);
      pending.clear();
      worker?.terminate();
      worker = undefined;
    },
  };
}
